import moment from 'moment';

import {
  getFinancialYearForStudyDate,
  parseStudyDateValue
} from './goalScopeValidation';

export interface ParsedReferenceYear {
  startYear?: string;
  endYear?: string;
}

const RANGE_OPTION_PATTERN = /^\d{4}-\d{4}$/;
const CALENDAR_YEAR_PATTERN = /^\d{4}$/;

export const financialYearStartYear = (financialYearValue: string): number =>
  parseInt(financialYearValue.split('-')[0], 10);

export const parseReferenceYear = (value?: string): ParsedReferenceYear => {
  const trimmed = value?.trim();

  if (!trimmed) {
    return {};
  }

  if (RANGE_OPTION_PATTERN.test(trimmed)) {
    const [startYear, endYear] = trimmed.split('-');

    return {startYear, endYear};
  }

  if (CALENDAR_YEAR_PATTERN.test(trimmed)) {
    return {startYear: trimmed, endYear: trimmed};
  }

  const parts = trimmed.split('-');

  if (parts.length === 1) {
    return {startYear: parts[0], endYear: parts[0]};
  }

  return {startYear: parts[0], endYear: parts[1]};
};

export const normalizeReferenceYearValue = (
  raw: unknown
): string[] | undefined => {
  if (raw == null) {
    return undefined;
  }

  if (Array.isArray(raw)) {
    const parts = raw.map((item) => String(item).trim()).filter(Boolean);

    if (parts.length >= 2) {
      return [parts[0], parts[1]];
    }

    if (parts.length === 1) {
      return [parts[0], parts[0]];
    }

    return undefined;
  }

  if (typeof raw === 'string' && raw.trim()) {
    const parsed = parseReferenceYear(raw.trim());

    if (parsed.startYear) {
      return [parsed.startYear, parsed.endYear ?? parsed.startYear];
    }
  }

  return undefined;
};

export const isValidReferenceYearPair = (start?: string, end?: string): boolean =>
  !!start?.trim() &&
  !!end?.trim() &&
  CALENDAR_YEAR_PATTERN.test(start.trim()) &&
  CALENDAR_YEAR_PATTERN.test(end.trim());

export const isStudyDateReady = (studyDateRaw?: string): boolean =>
  !!studyDateRaw?.trim() && !!parseStudyDateValue(studyDateRaw);

export const getStudyFinancialYearValue = (
  studyDateRaw?: string
): string | undefined => {
  if (!isStudyDateReady(studyDateRaw)) {
    return undefined;
  }

  const parsed = parseStudyDateValue(studyDateRaw ?? '');

  if (!parsed) {
    return undefined;
  }

  return getFinancialYearForStudyDate(parsed);
};

export const getStudyFinancialYearStartYear = (
  studyDateRaw?: string
): number | undefined => {
  const studyFinancialYear = getStudyFinancialYearValue(studyDateRaw);

  if (!studyFinancialYear) {
    return undefined;
  }

  return financialYearStartYear(studyFinancialYear);
};

export const getDefaultReferenceYear = (studyDateRaw: string): string[] => {
  const parsed = parseStudyDateValue(studyDateRaw);
  const year = String(parsed?.year() ?? moment().year());

  return [year, year];
};

export const isReferenceYearAllowedForStudyDate = (
  value: string | string[] | unknown,
  studyDateRaw?: string
): boolean => {
  const normalized = normalizeReferenceYearValue(value);

  if (!isValidReferenceYearPair(normalized?.[0], normalized?.[1]) || !normalized) {
    return false;
  }

  const referenceStartYear = parseInt(normalized[0], 10);
  const referenceEndYear = parseInt(normalized[1], 10);
  const currentYear = moment().year();

  if (Number.isNaN(referenceStartYear) || Number.isNaN(referenceEndYear)) {
    return false;
  }

  if (
    referenceEndYear < referenceStartYear ||
    referenceStartYear > currentYear ||
    referenceEndYear > currentYear
  ) {
    return false;
  }

  const studyFYStart = getStudyFinancialYearStartYear(studyDateRaw);

  if (studyFYStart == null) {
    return true;
  }

  return referenceStartYear >= studyFYStart;
};
